import React from 'react';
import cn from 'classnames';
import { PapiAttachmentFile, PapiDeviation, PapiExtendedDeviation } from '@wix/da-papi-types';
import AttachmentCaptionViewer from '@wix/da-editor-tiptap/pkg/components/Viewer/presets/AttachmentCaption';
import { FullViewProps, ImageSource } from '../../../DeviationViews/Full/types';
import Full from '../../../DeviationViews/Full';
import ImageAttachment from '../../../DeviationViews/Full/Image/ImageAttachment';
import TextRenderer from '../../../TextRenderer';

import s from './FullImageSourceWithCaption.scss';

export function isAttachmentFile(deviationOrAttachment: ImageSource): boolean {
  return (
    Object.hasOwn(deviationOrAttachment, 'filename') &&
    !Object.hasOwn(deviationOrAttachment, 'typeId')
  );
}

export type Caption = PapiExtendedDeviation['caption'];

// precondition: cpations are only one line and a fixed height
export const CAPTION_HEIGHT = 22;

function getImageSourceCaption(deviationOrAttachment: ImageSource): Caption | undefined {
  return isAttachmentFile(deviationOrAttachment)
    ? (deviationOrAttachment as PapiAttachmentFile).caption
    : (deviationOrAttachment as PapiDeviation).extended?.caption;
}

/** Full sized image deviation or attachment wrapped in a figure and with optional caption */
export interface FullImageSourceWithCaptionProps extends Omit<FullViewProps, 'deviation'> {
  deviationOrAttachment: ImageSource;
  id?: string;
  wrapperClassName?: string;
  anchorClassName?: string;
  captionClassName?: string;
  rendererClassName?: string;
  maintainHeightForCaptions?: boolean;
}

export const FullImageSourceWithCaption: React.FC<FullImageSourceWithCaptionProps> = ({
  deviationOrAttachment,
  id,
  wrapperClassName,
  anchorClassName,
  captionClassName,
  rendererClassName,
  maintainHeightForCaptions,
  height,
  ...viewProps
}) => {
  const isAttachment = isAttachmentFile(deviationOrAttachment);
  const caption = getImageSourceCaption(deviationOrAttachment);
  const hasCaption = !!caption?.excerpt;
  const adjHeight =
    (hasCaption || maintainHeightForCaptions) && height ? height - CAPTION_HEIGHT : height;

  return (
    <figure id={id} className={wrapperClassName}>
      {isAttachment ? (
        <ImageAttachment
          attachment={deviationOrAttachment as PapiAttachmentFile}
          {...viewProps}
          height={adjHeight}
        />
      ) : (
        <Full
          deviation={deviationOrAttachment as PapiDeviation}
          {...viewProps}
          height={adjHeight}
        />
      )}

      {hasCaption && (
        <figcaption className={cn(s['image-figcaption'], captionClassName)}>
          <TextRenderer
            className={cn(s['image-figcaption-renderer'], rendererClassName)}
            textContent={caption}
            tipTapViewer={AttachmentCaptionViewer}
          />
        </figcaption>
      )}
    </figure>
  );
};

export default FullImageSourceWithCaption;
